import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../common/Button';

const EVENT_COLORS = [
  { value: '#B47330', label: 'Dourado' },
  { value: '#492920', label: 'Marrom' },
  { value: '#C11E24', label: 'Vermelho' },
  { value: '#2196F3', label: 'Azul' },
  { value: '#4CAF50', label: 'Verde' },
  { value: '#9C27B0', label: 'Roxo' },
  { value: '#FF9800', label: 'Laranja' },
];

const EventForm = ({ initialData, onSubmit, isLoading }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || {
      title: '',
      description: '',
      location: '',
      startsAt: '',
      endsAt: '',
      allDay: false,
      color: '#B47330',
      status: 'draft',
    },
  });

  const allDay = watch('allDay');
  const startsAt = watch('startsAt');

  // O input alterna entre `date` e `datetime-local`, que aceitam formatos
  // diferentes. Sem normalizar, o valor vira inválido e o campo é zerado.
  useEffect(() => {
    if (!startsAt) return;

    const hasTime = startsAt.includes('T');

    if (allDay && hasTime) {
      setValue('startsAt', startsAt.split('T')[0]);
    } else if (!allDay && !hasTime) {
      setValue('startsAt', `${startsAt}T00:00`);
    }
  }, [allDay, startsAt, setValue]);

  const handleFormSubmit = (data) => {
    onSubmit({
      ...data,
      // Evento de dia inteiro não tem horário de término.
      endsAt: data.allDay ? '' : data.endsAt,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div className="md:col-span-2">
          <label htmlFor="title" className="label">
            Título *
          </label>
          <input
            id="title"
            type="text"
            className={`input ${errors.title ? 'input-error' : ''}`}
            {...register('title', {
              required: 'Título é obrigatório',
              minLength: {
                value: 3,
                message: 'Título deve ter no mínimo 3 caracteres',
              },
            })}
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
        </div>

        {/* All day */}
        <div className="md:col-span-2">
          <label htmlFor="allDay" className="flex items-center gap-2 cursor-pointer">
            <input
              id="allDay"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
              {...register('allDay')}
            />
            <span className="text-sm text-gray-700">Evento de dia inteiro</span>
          </label>
        </div>

        {/* Starts at */}
        <div>
          <label htmlFor="startsAt" className="label">
            {allDay ? 'Data *' : 'Início *'}
          </label>
          <input
            id="startsAt"
            type={allDay ? 'date' : 'datetime-local'}
            className={`input ${errors.startsAt ? 'input-error' : ''}`}
            {...register('startsAt', { required: 'Data de início é obrigatória' })}
          />
          {errors.startsAt && (
            <p className="mt-1 text-sm text-red-600">{errors.startsAt.message}</p>
          )}
        </div>

        {/* Ends at */}
        {!allDay && (
          <div>
            <label htmlFor="endsAt" className="label">
              Término
            </label>
            <input
              id="endsAt"
              type="datetime-local"
              className={`input ${errors.endsAt ? 'input-error' : ''}`}
              {...register('endsAt', {
                validate: (value) =>
                  !value ||
                  !startsAt ||
                  new Date(value) > new Date(startsAt) ||
                  'Término deve ser posterior ao início',
              })}
            />
            {errors.endsAt && <p className="mt-1 text-sm text-red-600">{errors.endsAt.message}</p>}
          </div>
        )}

        {/* Location */}
        <div>
          <label htmlFor="location" className="label">
            Local
          </label>
          <input
            id="location"
            type="text"
            className={`input ${errors.location ? 'input-error' : ''}`}
            {...register('location')}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
          )}
        </div>

        {/* Color */}
        <div>
          <label htmlFor="color" className="label">
            Cor no calendário
          </label>
          <select id="color" className="input" {...register('color')}>
            {EVENT_COLORS.map((color) => (
              <option key={color.value} value={color.value}>
                {color.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="label">
            Status *
          </label>
          <select
            id="status"
            className={`input ${errors.status ? 'input-error' : ''}`}
            {...register('status', { required: 'Status é obrigatório' })}
          >
            <option value="draft">Rascunho (não aparece no site)</option>
            <option value="published">Publicado (visível a todos)</option>
          </select>
          {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label htmlFor="description" className="label">
            Descrição
          </label>
          <textarea
            id="description"
            rows="4"
            className={`input ${errors.description ? 'input-error' : ''}`}
            {...register('description', {
              maxLength: {
                value: 2000,
                message: 'Descrição deve ter no máximo 2000 caracteres',
              },
            })}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={() => window.history.back()}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={isLoading}>
          {initialData ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </div>
    </form>
  );
};

export default EventForm;
